/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/

import { IModelApp, IModelConnection } from "@itwin/core-frontend";
import { ScreenViewport, Viewport, ViewState3d, StandardViewId } from "@itwin/core-frontend";

import { MarginPercent, MarginOptions, ViewChangeOptions, ZoomToOptions } from "@itwin/core-frontend";
import { ColorDef } from "@itwin/core-common";
import { Angle, Point3d, Range3d, Vector3d } from "@itwin/core-geometry";

import { cQuery } from "./Query";
import { cSelection } from "./Selection";
import { cClassifier } from "./Classifier";
import { cRealityData } from "./RealityData";

export class cView {
  private static iModel: IModelConnection;
  private static sViewPort: ScreenViewport;
  private static fitY: number;

  /* Setup the clip box */
  public static async initView(vp: ScreenViewport, imodel: IModelConnection) {
    this.iModel = imodel;
    this.sViewPort = vp;

    await this.setModelVisibility("", false);
    this.initViewSetting(vp);

    // await cMultiViewport.setupSingleViewport();
    // const svp = cView.getViewportById("ViewportRealityModel");
  }

  public static initViewSetting(vp: ScreenViewport) {
    if (!vp) return;
    
    vp.overrideDisplayStyle({
      backgroundColor: ColorDef.computeTbgrFromComponents(140, 140, 140),
    });

    vp.view.setStandardRotation(StandardViewId.Iso);

    vp.viewFlags = vp.viewFlags.with("clipVolume", true);
    vp.viewFlags = vp.viewFlags.with("backgroundMap", false);
    vp.viewFlags = vp.viewFlags.with("grid", false);

    vp.turnCameraOn();

    const view3d = vp.view as ViewState3d;
    this.fitY = view3d.extents.y;
  }

  public static showCADModel() {
    cView.setModelVisibility("$CAD-Model", true);
    cClassifier.removeClassifiers(); //!!OA - turn off classifier due to bug
  }

  public static hideCADModel() {
    cView.setModelVisibility("$CAD-Model", false);
    cClassifier.setupClassifier("$Elements");
  }

  public static async fetchModelIds(modelName : string) {
    const modelIds : string[] = [];

    const curModelProps = await cQuery.fetchModelProps(modelName);
    for (const modelProps of curModelProps) {
      modelIds.push(modelProps.id!);
    }

    return modelIds;  
  }

  public static async setModelVisibility(modelName: string, viewFlag: boolean) {
    const curModelIds = await this.fetchModelIds(modelName);
    const vp = cView.getCurrentView();
    for (const modelId of curModelIds) {
      vp.changeModelDisplay(modelId, viewFlag);
    }
  }

  public static async zoomToElement(elementId: string[], viewPort?: Viewport, margin = 0.02) {
    if (!elementId) return;

    const vp = viewPort ?? cView.getCurrentView();
    const viewChangeOpts: ViewChangeOptions & MarginOptions = {
      animateFrustumChange: true,
      marginPercent: new MarginPercent(margin, margin, margin, margin),
    };
    const zoomToOpts: ZoomToOptions = {};

    await vp.zoomToElements(elementId, { ...viewChangeOpts, ...zoomToOpts });
  }

  public static zoomFactor(): number {
//    const view3d = this.sViewPort.view as ViewState3d;
    const vp = cView.getCurrentView();  //!! OA view
    const view3d = vp.view as ViewState3d;  //!! OA view
    const currY = view3d.extents.y;
    const zoomFac = this.fitY / currY;

    return zoomFac;
  }

  public static async zoomToTag(tag: string): Promise<string[] | undefined> {

    const elementIds = await cQuery.getElementIdFromTag(tag);
    if (elementIds) {
      await cView.zoomToElement(elementIds);
    }

    return elementIds;
  }

  public static async makeMask(maskElem: Set<string>) {
    const models = cRealityData.getRealityModelIds();
    const maskModel = models[models.length - 1];
    cRealityData.attachMask(maskModel, maskElem);
  }

  public static async removeMask() {
    
    //const model = cRealityData.getActiveRealityModelName();
     const models = cRealityData.getRealityModelIds();
     const maskModel = models[models.length - 1];
    // cRealityData.removeRealityModel();
    // cRealityData.attachRealityModel(model);
    cRealityData.removeMask(maskModel);
  }

  public static async zoomToRange(range: Range3d) {
    if (range && !range.isNull) {
      const vp = cView.getCurrentView();
      const viewChangeOpts: ViewChangeOptions & MarginOptions = {
        animateFrustumChange: true,
        marginPercent: new MarginPercent(0.02, 0.02, 0.02, 0.02),
      };
      vp.zoomToVolume(range, viewChangeOpts);
    }
  }

  public static async zoomToSelection() {

    const elements = cSelection.getSelections();
    const vp = cView.getCurrentView();
    await vp.zoomToElements(elements, { animateFrustumChange: true });
  }

  public static getCameraProps(eye: Point3d, target: Point3d) {

    const vp = cView.getCurrentView();
    target.setFrom(vp.view.getTargetPoint());
    eye.setFrom(vp.getFrustum()?.getEyePoint());
  }

  public static setCameraView(eye: Point3d, target: Point3d, up?: Vector3d) {
    const viewChangeOpts: ViewChangeOptions = { animateFrustumChange: true };

    const upVector = up ?? new Vector3d(0, 0, 1);
    const fov = Angle.degreesToRadians(90);

    const vp = cView.getCurrentView();
    const view3d = vp.view as ViewState3d;

    view3d.lookAt({
      eyePoint: eye,
      targetPoint: target,
      upVector: upVector,
      lensAngle: Angle.createRadians(fov),
      frontDistance: undefined,
      backDistance: undefined,
      opts: viewChangeOpts,
    });

    //!!OA view    cView.sViewPort.synchWithView(viewChangeOpts);
    vp.synchWithView(viewChangeOpts);  //!!OA view
  }

  public static getCurrentView(): ScreenViewport {
    return IModelApp.viewManager.selectedView!;
  }

  public static setCurrentView(vp: ScreenViewport) {
    if (vp) {
      IModelApp.viewManager.setSelectedView(vp);
    }
  }

  // helper function to get viewport
  // public static getActiveViewports(vsType : number = 0): Viewport[] {
  //   const vpList : Viewport[] = [];

  //   for (const vp of IModelApp.viewManager) {
  //     if (vsType == 2) {
  //       if (vp.view.is2d()) vpList.push(vp);
  //     } else if (vsType == 3) {
  //       if (vp.view.is3d()) vpList.push(vp);
  //     } else {
  //       vpList.push(vp);
  //     }
  //   }

  //   return vpList;
  // }

  // public static getViewportById(vpId : string): ScreenViewport {
  //   let vp : ScreenViewport;

  //   const activeFrontstage = FrontstageManager.activeFrontstageDef!;
  //   for (const aFs of activeFrontstage.contentControls) {
  //     if (aFs.name.includes(vpId)) {
  //       vp = aFs.viewport!;
  //     }
  //   }

  //   return vp!;
  // }
}